import { useEffect, useMemo, useState } from 'react'
import { scenarios } from '@/core/data'
import { resolveCite, ruleCitation, ruleText } from '@/core/sources'
import type { Key } from '@/core/i18n'
import { useSettings } from './settings'
import type { Subject } from './Viewer'

export interface ScenariosProps {
  scenarioId: string
  onSelect: (id: string) => void
  onSubject: (subject: Subject) => void
}

const ORDER = ['own', 'other', 'both', 'neither'] as const

function giveWayKey(option: string): Key {
  return `giveway.${option}` as Key
}

/**
 * Ausweichsituationen: die Szene zeigt, was von der eigenen Brücke aus zu sehen ist,
 * die Frage lautet immer gleich, und die Auflösung nennt die Regel im Wortlaut.
 */
export function Scenarios({ scenarioId, onSelect, onSubject }: ScenariosProps) {
  const { t, pick } = useSettings()
  const [answer, setAnswer] = useState<string | undefined>()
  const scenario = useMemo(
    () => scenarios.find((s) => s.id === scenarioId) ?? scenarios[0]!,
    [scenarioId],
  )

  useEffect(() => {
    onSubject({ kind: 'scenario', id: scenario.id })
    setAnswer(undefined)
  }, [scenario.id, onSubject])

  return (
    <section className="scenarios" data-testid="scenarios">
      <h2 className="quiz__prompt">{t('scenarios.prompt')}</h2>
      {scenario.windFromDeg !== undefined && (
        <p className="detail__text" data-testid="scenario-wind">
          {t('scenarios.wind', { wind: scenario.windFromDeg, heading: scenario.own.headingDeg })}
        </p>
      )}
      <div className="quiz__options">
        {ORDER.map((option) => (
          <button
            key={option}
            type="button"
            className={`quiz__option${
              answer === undefined
                ? ''
                : option === scenario.giveWay
                  ? ' quiz__option--right'
                  : option === answer
                    ? ' quiz__option--wrong'
                    : ''
            }`}
            data-answer={option}
            disabled={answer !== undefined}
            onClick={() => setAnswer(option)}
          >
            {t(giveWayKey(option))}
          </button>
        ))}
      </div>

      {answer !== undefined && (
        <div className="quiz__result" data-testid="scenario-result">
          <p className="quiz__verdict" data-correct={answer === scenario.giveWay ? 'ja' : 'nein'}>
            {answer === scenario.giveWay
              ? t('quiz.right')
              : t('quiz.wrong', { answer: t(giveWayKey(scenario.giveWay)) })}
          </p>
          <p className="detail__text">
            <strong>{pick(scenario.action)}</strong>
          </p>
          <p className="detail__text">{pick(scenario.explanation)}</p>
          <h3 className="detail__legend">{ruleCitation(scenario.rule, scenario.cite)}</h3>
          <ul className="detail__quotes">
            {scenario.cite.map((entry) => {
              const found = resolveCite(scenario.rule, entry, scenario.scope)
              return (
                <li key={entry}>
                  {found.rule !== scenario.rule && <strong>Rule {found.rule}: </strong>}
                  {ruleText(found.paragraph)}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <nav className="catalogue" data-testid="scenario-catalogue">
        <div className="catalogue__items">
          {scenarios.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`states__item${s.id === scenario.id ? ' states__item--on' : ''}`}
              data-scenario={s.id}
              onClick={() => onSelect(s.id)}
            >
              <span className="states__rule">Rule {s.rule}</span>
              <span className="states__title">{pick(s.title)}</span>
            </button>
          ))}
        </div>
      </nav>
    </section>
  )
}
